/**
 * Service de gestion des appels d'urgence et d'envoi de messages via EmailJS API.
 * Respecte strictement la sécurité des identifiants (ne divulgue jamais la clé privée).
 */

const EMAILJS_API_URL = "https://api.emailjs.com/api/v1.0/email/send";

/**
 * Récupère les identifiants publics configurés dans l'environnement
 */
function getEmailConfig() {
  const publicKey = process.env.REACT_APP_MAIL_JS_PBK || process.env.MAIL_JS_PBK || "YcVljh_B17I-gjms7";
  const templateId = process.env.REACT_APP_MAIL_JS_TMP_ID || process.env.MAIL_JS_TMP_ID || "template_3uhjz28";
  const serviceId = process.env.REACT_APP_MAIL_JS_SERVICE_ID || process.env.MAIL_JS_SERVICE_ID || "default_service";

  return {
    publicKey: publicKey ? publicKey.trim() : "",
    templateId: templateId ? templateId.trim() : "",
    serviceId: serviceId ? serviceId.trim() : "default_service",
  };
}

/**
 * Transmet une demande d'urgence / message de contact à la fonction d'urgence
 * @param {Object} payload - { name, email, objet, time, message, ... }
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function sendEmergencyRequest(payload) {
  const config = getEmailConfig();

  if (!config.publicKey || !config.templateId) {
    console.error("Configuration d'urgence incomplète dans l'environnement.");
    return {
      success: false,
      error: "La configuration du service d'urgence est indisponible. Veuillez contacter l'administration.",
    };
  }

  // Préparation de la requête EmailJS
  const requestBody = {
    service_id: config.serviceId,
    template_id: config.templateId,
    user_id: config.publicKey, // Clé publique uniquement
    template_params: payload,
  };

  // Contrôleur d'annulation pour gérer le timeout (10 secondes)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(EMAILJS_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok || response.status === 200) {
      return {
        success: true,
        message: "Votre signalement a été transmis avec succès à la cellule de permanence de l'ACEM Fès.",
      };
    } else {
      const errorText = await response.text().catch(() => "Erreur inconnue du serveur");
      console.warn("Échec de l'appel d'urgence:", response.status, errorText);

      // Si le service par défaut échoue et que nous avons un fallback, tentons avec un service générique
      if (config.serviceId === "default_service") {
        return await retryWithFallbackService(requestBody, config.publicKey, config.templateId);
      }

      return {
        success: false,
        error: `Impossible d'acheminer le message (code ${response.status}). Veuillez contacter directement le bureau par téléphone.`,
      };
    }
  } catch (err) {
    clearTimeout(timeoutId);

    if (err.name === "AbortError") {
      return {
        success: false,
        error: "Le délai d'attente de la demande a expiré. Veuillez vérifier votre connexion internet.",
      };
    }

    console.error("Erreur de connexion avec la fonction d'urgence:", err);
    return {
      success: false,
      error: "Erreur de connexion réseau lors de l'envoi. Veuillez vérifier votre réseau et réessayer.",
    };
  }
}

/**
 * Tentative de secours en cas de besoin de fallback de service ID
 */
async function retryWithFallbackService(requestBody, publicKey, templateId) {
  const fallbackServices = ["service_acem", "gmail"];
  for (const serviceId of fallbackServices) {
    try {
      const retryBody = { ...requestBody, service_id: serviceId };
      const res = await fetch(EMAILJS_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(retryBody),
      });

      if (res.ok) {
        return {
          success: true,
          message: "Votre message a été transmis avec succès.",
        };
      }
    } catch (e) {
      // continuer la boucle
    }
  }

  return {
    success: false,
    error: "Erreur lors de l'acheminement du message. Veuillez contacter l'ACEM Fès via les numéros répertoriés.",
  };
}
